import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import classes from './ContractsChoiceModal.module.scss';

interface ContractsChoiceModalProps {
  onClose: () => void;
  onPickPublic: () => void;
  onPickPersonal: () => void;
  hasPublicBuilding: boolean;
  // Link target for "ask the AI concierge a question" under the public
  // option — the ask page for the relevant building, with ?focus=1 so it
  // focuses the composer without pre-filling a question. Each caller
  // already resolves the building slug for onPickPublic's navigate(), so
  // it passes the same slug through here rather than the modal guessing.
  askHref?: string;
}

const ContractsChoiceModal: React.FC<ContractsChoiceModalProps> = ({
  onClose,
  onPickPublic,
  onPickPersonal,
  hasPublicBuilding,
  askHref,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={classes.backdrop} onClick={handleBackdropClick}>
      <div className={classes.modal} role="dialog" aria-modal="true" aria-labelledby="contracts-choice-title">
        <div className={classes.header}>
          <h2 id="contracts-choice-title">Which contracts?</h2>
          <button type="button" className={classes.closeButton} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className={classes.body}>
          <div className={classes.choiceCard}>
            <button
              type="button"
              className={classes.choiceCardBtn}
              onClick={onPickPublic}
              disabled={!hasPublicBuilding}
            >
              <span className={classes.choiceTitleRow}>
                <span className={classes.choiceTitle}>AI concierge</span>
              </span>
              <span className={classes.choiceSub}>
                Information relevant for your whole building: contracts,
                by-laws, budgets, meeting minutes, insurance and compliance
                paperwork shared across residents, owners and providers.
              </span>
              {!hasPublicBuilding && (
                <span className={classes.choiceHint}>
                  Open a building first to use the AI concierge.
                </span>
              )}
            </button>
            <p className={classes.choiceNote}>
              Documents are only viewable and managed by Operators. Other
              residents can see what's been uploaded but not open the files.{' '}
              {askHref ? (
                <Link to={askHref} className={classes.choiceNoteLink} onClick={onClose}>
                  ask the AI concierge a question
                </Link>
              ) : (
                'ask the AI concierge a question'
              )}{' '}
              instead for a redacted, accurate response.
            </p>
          </div>

          <button
            type="button"
            className={classes.choice}
            onClick={onPickPersonal}
          >
            <span className={classes.choiceTitleRow}>
              <span className={classes.choiceTitle}>Contracts AI</span>
              <span className={`${classes.scopeTag} ${classes['scopeTag--private']}`}>Private</span>
            </span>
            <span className={classes.choiceSub}>
              Contracts relevant to only you: your lease, quotes, invoices,
              personal correspondence. Nobody else can read them.
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractsChoiceModal;
